import { consume } from "@lit/context";
import {
    Contract,
    CreateOrUpdateJobPostingParams,
    EmailTemplateDTO,
    type EmailTemplateType,
    Employee,
    type EmploymentType,
    type JobApplicationDTO,
    JobApplicationStatus,
    JobPostingDTO,
    JobPostingStatus,
    MimeType,
    Position,
    type PositionDTO,
    RejectJobApplicationDTO,
    Salary,
    employmentTypeLabel,
    formatDate,
    formatNumber,
    getEmploymentTypes,
    toDateString,
} from "@pentacode/core";
import type { Euros, Hours } from "@pentacode/openapi";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import "../../../components/rich-text/rich-content";
import type { RichContent } from "../../../components/rich-text/rich-content";
import type { RichTextEditor } from "../../../components/rich-text/rich-text-editor";
import { type EnabledFeatures, enabledFeatures } from "../../../contexts/features-context";
import { alert, confirm } from "../../../elements/alert-dialog";
import { DateInput } from "../../../elements/date-input";
import "../../../elements/entity-multi-select";
import type { EntityMultiSelect } from "../../../elements/entity-multi-select";
import "../../../elements/new-employee-dialog";
import type { NewEmployeeDialog } from "../../../elements/new-employee-dialog";
import "../../../elements/popover";
import "../../../elements/scroller";
import { PUBLIC_PTC_JOBS_URL } from "../../../env";
import { app, router } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { readFileAsDataURL } from "../../../lib/util";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "./job-application-dialog";
import type { JobApplicationDialog } from "./job-application-dialog";
import "./job-application-preview";
import "./job-posting-email-template-dialog";
import type { JobPostingEmailTemplateDialog } from "./job-posting-email-template-dialog";

const statusTitle: Record<JobApplicationStatus, string> = {
    [JobApplicationStatus.Open]: "Offene Bewerbungen",
    [JobApplicationStatus.Accepted]: "Akzeptierte Bewerbungen",
    [JobApplicationStatus.Rejected]: "Abgelehnte Bewerbungen",
};

const statusLabel: Record<JobApplicationStatus, string> = {
    [JobApplicationStatus.Open]: "offenen Bewerbungen",
    [JobApplicationStatus.Accepted]: "akzeptierten Bewerbungen",
    [JobApplicationStatus.Rejected]: "abgelehnten Bewerbungen",
};

// type JobApplication = JobPostingDTO["applications"][number];

@customElement("ptc-recruiting-posting")
export class RecruitingPosting extends StateMixin(LitElement) {
    @property({ attribute: false })
    posting: JobPostingDTO;

    @consume({ context: enabledFeatures, subscribe: true })
    private _enabledFeatures: EnabledFeatures;

    @state()
    private _loading = false;

    @state()
    private _editing = false;

    @state()
    private _images: string[] = [];

    @state()
    private _selectedVenueId: number | null = null;

    @query("input[name='title']")
    private _titleInput: HTMLInputElement;

    @query("select[name='venueId']")
    private _venueIdSelect: HTMLSelectElement;

    @query("select[name='employmentType']")
    private _employementTypeSelect: HTMLSelectElement;

    @query("select[name='payType']")
    private _payTypeSelect: HTMLSelectElement;

    @query("input[name='payAmount']")
    private _payAmountInput: HTMLInputElement;

    @query("input[name='hoursPerWeek']")
    private _hoursPerWeekInput: HTMLInputElement;

    @query("ptc-date-input[name='start']")
    private _startInput: DateInput;

    @query("ptc-date-input[name='end']")
    private _endInput: DateInput;

    @query("ptc-entity-multi-select")
    private _positionsSelector: EntityMultiSelect<Position>;

    @query("ptc-rich-text-editor")
    private _descriptionEditor: RichTextEditor;

    @query("ptc-rich-content")
    private _descriptionContainer: RichContent;

    @query(".sidebar-inner")
    private _sidebarInner: HTMLDivElement;

    @query("input[type='file']")
    private _imageInput: HTMLInputElement;

    @singleton("ptc-job-application-dialog")
    private _jobApplicationDialog: JobApplicationDialog;

    @singleton("ptc-new-employee-dialog")
    private _newEmployeeDialog: NewEmployeeDialog;

    @singleton("ptc-job-posting-email-template-dialog")
    private _setEmailTemplateDialog: JobPostingEmailTemplateDialog;

    get editing(): boolean {
        return this._editing;
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("posting")) {
            this._editing = !this.posting.id;
            this._selectedVenueId = this.posting.venueId;

            if (this._editing) {
                void this.updateComplete.then(() => this._refreshForm());
            }
        }

        setTimeout(() => {
            if (this._descriptionContainer) {
                this._descriptionContainer.previewHeight = Math.max(this._sidebarInner.offsetHeight - 80, 0);
            }
        }, 100);
    }

    async edit() {
        this._editing = true;
        await this.updateComplete;
        this._refreshForm();
    }

    cancelEdit() {
        this._editing = false;
        this.dispatchEvent(new CustomEvent(this.posting.id ? "posting-edit-canceled" : "posting-create-canceled"));
    }

    private _refreshForm() {
        if (!this._titleInput) {
            return;
        }
        this._titleInput.value = this.posting.title;
        this._startInput.value = this.posting.start;
        this._endInput.value = this.posting.end;
        this._payAmountInput.value = this.posting.payAmount?.toFixed(2) || "";
        this._payTypeSelect.value = this.posting.payType;
        this._venueIdSelect.value = this.posting.venueId.toString();
        this._descriptionEditor.content = this.posting.description;
        this._positionsSelector.selected = (this.posting.positions || []).map(
            (p: Position) => new Position(p as Partial<Position>)
        );
        this._employementTypeSelect.value = this.posting.employmentType.toString();
        this._hoursPerWeekInput.value = this.posting.hoursPerWeek?.toFixed(2) || "";
        this._images = this.posting.imageUrls;
    }

    private _buildCreateOrUpdateJobPostingParams(): CreateOrUpdateJobPostingParams {
        const title = this._titleInput?.value?.trim() ?? this.posting.title ?? "";
        const startValue = this._startInput?.value;
        const start = startValue === undefined ? (this.posting.start ?? null) : startValue || null;
        const endValue = this._endInput?.value;
        const end = endValue === undefined ? (this.posting.end ?? null) : endValue || null;
        const positions =
            this._positionsSelector?.selected?.map((p) => p.id) ??
            (this.posting.positions || []).map((position) => position.id);
        const description = this._descriptionEditor?.content ?? this.posting.description;
        const payTypeValue = this._payTypeSelect?.value as "monthly" | "hourly" | undefined;
        const payType = payTypeValue ?? this.posting.payType;
        const payAmountRaw = this._payAmountInput?.value;
        const payAmount = (
            payAmountRaw === undefined ? (this.posting.payAmount ?? 0) : Number(payAmountRaw) || 0
        ) as Euros;
        const venueIdRaw = this._venueIdSelect?.value;
        const venueId = venueIdRaw === undefined ? this.posting.venueId : Number(venueIdRaw);
        const hoursPerWeekRaw = this._hoursPerWeekInput?.value;
        const hoursPerWeek =
            hoursPerWeekRaw !== undefined && hoursPerWeekRaw !== ""
                ? (Number(hoursPerWeekRaw) as Hours)
                : hoursPerWeekRaw === undefined
                  ? this.posting.hoursPerWeek
                  : null;
        const employmentTypeRaw = this._employementTypeSelect?.value;
        const employmentType =
            employmentTypeRaw !== undefined
                ? (Number(employmentTypeRaw) as EmploymentType)
                : this.posting.employmentType;

        return new CreateOrUpdateJobPostingParams({
            id: this.posting.id || undefined,
            title,
            start,
            end,
            description,
            positions,
            payType,
            payAmount,
            venueId,
            hoursPerWeek,
            employmentType,
            images: this._images,
            submissionConfirmationEmailTemplate: this.posting.submissionConfirmationEmailTemplate
                ? {
                      title: this.posting.submissionConfirmationEmailTemplate.title,
                      body: this.posting.submissionConfirmationEmailTemplate.body,
                      replyTo: this.posting.submissionConfirmationEmailTemplate.replyTo,
                  }
                : undefined,
            rejectionEmailTemplate: this.posting.rejectionEmailTemplate
                ? {
                      title: this.posting.rejectionEmailTemplate.title,
                      body: this.posting.rejectionEmailTemplate.body,
                      replyTo: this.posting.rejectionEmailTemplate.replyTo,
                  }
                : undefined,
        });
    }

    private async _submit(e: Event) {
        e.preventDefault();

        this._loading = true;

        try {
            const isNew = !this.posting.id;
            this.posting = await app.api.createOrUpdateJobPosting(this._buildCreateOrUpdateJobPostingParams());
            this._editing = false;
            this._images = [];

            this.dispatchEvent(
                new CustomEvent(isNew ? "posting-created" : "posting-edited", { detail: { posting: this.posting } })
            );
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    connectedCallback(): void {
        super.connectedCallback();
        // tiptap (~564KB, vendor-editor) loads on demand with the recruiting editor.
        void import("../../../components/rich-text/rich-text-editor");
        this._selectedVenueId = this.posting.venueId;
    }

    private async _archivePosting() {
        if (
            !(await confirm(
                "Sind Sie sicher, dass Sie diese Stellenausschreibung und alle Verbundenen Bewerbungen archivieren möchten?",
                "Archivieren",
                "Abbrechen",
                { type: "destructive", icon: "icon.action.archive", title: "Ausschreibung Archivieren" }
            ))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.api.archiveJobPosting(this.posting.id as number);
            this.dispatchEvent(new CustomEvent("posting-archived", { detail: { posting: this.posting } }));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _copyPosting() {
        const posting = new JobPostingDTO({
            ...this.posting,
            id: null,
            uuid: "",
            status: JobPostingStatus.Published,
            imageUrls: [...this.posting.imageUrls],
            submissionConfirmationEmailTemplate: this.posting.submissionConfirmationEmailTemplate
                ? new EmailTemplateDTO({ ...this.posting.submissionConfirmationEmailTemplate })
                : undefined,
            rejectionEmailTemplate: this.posting.rejectionEmailTemplate
                ? new EmailTemplateDTO({ ...this.posting.rejectionEmailTemplate })
                : undefined,
            positions: [...(this.posting.positions || [])],
            applications: [],
        });

        this.dispatchEvent(new CustomEvent("posting-copy-created", { detail: { posting } }));
    }

    private async _reactivatePosting() {
        const action = await alert("Möchten Sie die bestehende Ausschreibung reaktivieren?", {
            icon: "icon.action.reset",
            title: "Ausschreibung Reaktivieren",
            optionsLayout: "vertical",
            options: [
                {
                    label: "Bestehende Ausschreibung Reaktivieren",
                    value: "reactivate",
                    buttonClass: "primary",
                },
                { label: "Abbrechen", value: "cancel", buttonClass: "transparent" },
            ],
        });

        if (action !== "reactivate" || !this.posting.id) {
            return;
        }

        this._loading = true;
        try {
            await app.api.reactivateJobPosting(this.posting.id);
            this.dispatchEvent(new CustomEvent("posting-reactivated", { detail: { posting: this.posting } }));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _deletePosting() {
        if (
            !(await confirm(
                "Sind Sie sicher, dass Sie diese Stellenausschreibung und alle verbundenen Bewerbungen unwiderruflich löschen möchten? Dieser Vorgang kann nicht rückgängig gemacht werden.",
                "Löschen",
                "Abbrechen",
                { type: "destructive", icon: "icon.action.delete", title: "Ausschreibung Löschen" }
            ))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.api.deleteJobPosting(this.posting.id as number);
            this.dispatchEvent(new CustomEvent("posting-deleted", { detail: { posting: this.posting } }));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _openApplication(application: JobApplicationDTO) {
        const result = await this._jobApplicationDialog.show({ application, posting: this.posting });
        if (!result) {
            return;
        }

        this.posting.applications = this.posting.applications.map((a) =>
            a.id === application.id ? { ...a, notes: result.notes } : a
        );
        this.requestUpdate();

        if (result.action === "approve") {
            const positions: Position[] = (this.posting.positions ?? []).flatMap((p: PositionDTO) => {
                if (p?.id) {
                    const res = app.getPosition(p.id);
                    if (!res) return [];
                    return [res.position ?? null];
                }
                return [];
            });

            const employee = await this._newEmployeeDialog.show(
                new Employee({
                    firstName: application.firstName,
                    lastName: application.lastName,
                    avatar: application.headShotUrl,
                    birthday: application.birthday || undefined,
                    address: application.address,
                    postalCode: application.postalCode,
                    city: application.city,
                    email: application.email,
                    phone: application.phone,
                    positions: positions,
                    contracts: [
                        new Contract({
                            start: this.posting.start || toDateString(new Date()),
                            end: this.posting.end,
                            hoursPerWeek: this.posting.hoursPerWeek || (0 as Hours),
                            employmentType: this.posting.employmentType,
                            salaries: [
                                new Salary({
                                    type: this.posting.payType,
                                    amount: this.posting.payAmount,
                                    positionId: null,
                                }),
                            ],
                        }),
                    ],
                })
            );
            if (employee) {
                await this._acceptApplication(application.id, employee.id);
                router.go(`employees/${employee.id}`);
            }
        } else if (result.action === "reject") {
            await this._rejectApplication(application.id, result.sendEmail);
            this.dispatchEvent(new CustomEvent("posting-edited", { detail: { posting: this.posting } }));
        } else if (result.action === "delete") {
            await this._deleteApplication(application.id);
            this.dispatchEvent(new CustomEvent("posting-edited", { detail: { posting: this.posting } }));
        }
    }

    private async _rejectApplication(applicationId: number, sendEmail: boolean) {
        this._loading = true;
        try {
            await app.api.rejectJobApplication(
                new RejectJobApplicationDTO({
                    applicationId,
                    sendEmail,
                } as RejectJobApplicationDTO)
            );
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _deleteApplication(applicationId: number) {
        this._loading = true;
        try {
            await app.api.deleteJobApplication(applicationId);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _acceptApplication(applicationId: number, employeeId: number) {
        this._loading = true;
        try {
            await app.api.acceptJobApplication({ applicationId, employeeId });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private _selectImage() {
        this._imageInput.value = "";
        this._imageInput.click();
    }

    private async _imageSelected() {
        const file = this._imageInput?.files?.[0];
        if (file) {
            if (file && file.size > 2e6) {
                void alert("Bilder dürfen nicht größer als 2MB sein!", { title: "Datei zu groß", type: "warning" });
                this._imageInput.value = "";
                return;
            }
            this._images.push(await readFileAsDataURL(file));
            this.requestUpdate();
        }
    }

    private async _removeImage(index: number) {
        this._images.splice(index, 1);
        this.requestUpdate();
    }

    private async _openEmailTemplate(type: EmailTemplateType) {
        const defaultTitle = type === "submission" ? "Bewerbung eingegangen" : "Bewerbung abgelehnt";
        const templateFieldByType = {
            submission: "submissionConfirmationEmailTemplate",
            rejection: "rejectionEmailTemplate",
        } as const;

        const templateField = templateFieldByType[type];

        const template = new EmailTemplateDTO({
            title: this.posting[templateField]?.title ?? defaultTitle,
            body: this.posting[templateField]?.body ?? "",
            replyTo: this.posting[templateField]?.replyTo ?? "",
        });

        const result = await this._setEmailTemplateDialog.show({ template, posting: this.posting });
        if (result?.action === "save" && result.template) {
            const previousTemplate = this.posting[templateField];
            this.posting[templateField] = result.template;
            this.requestUpdate();

            if (!this.posting.id) {
                return;
            }

            this._loading = true;
            try {
                this.posting = await app.api.createOrUpdateJobPosting(this._buildCreateOrUpdateJobPostingParams());
                this.dispatchEvent(new CustomEvent("posting-edited", { detail: { posting: this.posting } }));
            } catch (e) {
                this.posting[templateField] = previousTemplate;
                this.requestUpdate();
                void alert(e.message, { type: "warning" });
            }
            this._loading = false;
        }
    }

    private _getTemplatePreviewText(body?: string | null) {
        if (!body) {
            return "";
        }

        const parsed = new DOMParser().parseFromString(body, "text/html");
        return (parsed.body.textContent ?? "").replace(/\s+/g, " ").trim();
    }

    private _renderTemplatePreview(template: EmailTemplateDTO | undefined, type: EmailTemplateType) {
        const title = template?.title?.trim() ?? "";
        const bodyText = this._getTemplatePreviewText(template?.body);
        const hasTemplate = Boolean(title || bodyText);
        const bodyPreview = bodyText.length > 120 ? `${bodyText.slice(0, 117)}...` : bodyText;

        if (!hasTemplate) {
            return html`
                <button
                    class="mail-template-preview empty subtle"
                    type="button"
                    @click=${() => this._openEmailTemplate(type)}
                >
                    <ptc-ui-icon name="icon.action.send-message" class="big"></ptc-ui-icon>
                </button>
            `;
        }

        return html`
            <button
                class="mail-template-preview with-template subtle"
                type="button"
                @click=${() => this._openEmailTemplate(type)}
            >
                <div class="horizontal start-aligning spacing layout">
                    <ptc-ui-icon name="icon.object.comment" class="big"></ptc-ui-icon>
                    <div class="stretch vertical layout">
                        <div class="small subtle template-preview-body">
                            ${bodyPreview || "Keine Nachricht hinterlegt"}
                        </div>
                    </div>
                </div>
            </button>
        `;
    }

    static styles = [
        shared,
        DateInput.styles,
        css`
            :host {
                position: relative;
            }

            input.pay-amount {
                border-radius: var(--border-radius) 0 0 var(--border-radius);
                text-align: right;
            }

            .pay-type {
                border-left: none;
                border-radius: 0 var(--border-radius) var(--border-radius) 0;
            }

            .sidebar {
                width: 21em;
            }

            label {
                color: var(--color-primary);
            }

            .applications .grid {
                grid-template-columns: repeat(auto-fill, minmax(var(--grid-column-width, 16em), 1fr));
            }

            .images {
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-gap: 0.5em;
            }

            .image-container {
                padding-bottom: 75%;
                position: relative;
            }

            .image-container img {
                position: absolute;
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center center;
                border-radius: 0.5em;
            }

            .image-container button {
                position: absolute;
                top: -1em;
                right: -1em;
                background: var(--color-bg) !important;
                opacity: 1 !important;
                padding: 0.2em;
                z-index: 1;
            }

            .image-container:not(:hover) button {
                display: none;
            }

            .mail-template-buttons {
                display: flex;
                align-items: center;
            }

            .mail-template-preview {
                margin-top: 0.35em;
            }

            .mail-template-preview.empty {
                width: calc((100% - 0.5em) / 2);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .mail-template-preview.with-template {
                width: 100%;
                text-align: left;
            }

            .mail-template-preview.with-template .horizontal {
                width: 100%;
            }

            .mail-template-preview.with-template .stretch {
                min-width: 0;
            }

            .mail-template-preview .template-preview-body {
                display: block;
                width: 100%;
                margin-top: 0.15em;
                line-height: 1.3;
                white-space: normal;
                overflow-wrap: anywhere;
                word-break: break-word;
            }
        `,
    ];

    private _renderEditing() {
        return html`
            <form class="fullbleed vertical layout" @submit=${this._submit}>
                <div class="padded spacing horizontal layout stretch">
                    <div class="stretch vertical spacing layout">
                        <div class="vertical layout">
                            <input
                                placeholder="Ausschreibungs-Titel"
                                class="large slim"
                                name="title"
                                required
                                maxlength="100"
                            />
                        </div>
                        <div class="stretch vertical layout">
                            <label>Beschreibung</label>
                            <div class="stretch collapse relative">
                                <ptc-rich-text-editor class="fit-vertically"></ptc-rich-text-editor>
                            </div>
                        </div>
                        <div class="spacing evenly stretching horizontal layout">
                            <button class="primary">Speichern</button>
                            <button class="transparent" type="button" @click=${this.cancelEdit}>Abbrechen</button>
                        </div>
                    </div>
                    <div class="sidebar vertical layout">
                        <ptc-scroller class="stretch collapse">
                            <div class="padded">
                                <div class="field">
                                    <label>Standort</label>

                                    <select
                                        name="venueId"
                                        @change=${(e: Event) => {
                                            this._selectedVenueId = Number((e.target as HTMLSelectElement).value);

                                            // clear selected positions as they cannot be used in the new venue
                                            this._positionsSelector.selected = [];
                                        }}
                                    >
                                        ${app.accessibleVenues.map(
                                            (venue) => html`
                                                <option .value=${venue.id.toString()}>${venue.name}</option>
                                            `
                                        )}
                                    </select>
                                </div>
                                <div class="field">
                                    <label>Beschäftigungsverhältnis</label>

                                    <select name="employmentType">
                                        ${getEmploymentTypes().map(
                                            ({ type, label }) => html`
                                                <option .value=${type.toString()}>${label}</option>
                                            `
                                        )}
                                    </select>
                                </div>

                                <div class="field">
                                    <label>Positionen</label>

                                    <ptc-entity-multi-select
                                        .existing=${this._selectedVenueId
                                            ? (app
                                                  .getVenue(this._selectedVenueId)
                                                  ?.departments.flatMap((d) => d.positions)
                                                  .filter((position) => app.hasAccess({ position })) ?? [])
                                            : []}
                                        .getId=${((pos: unknown) => (pos as Position).id) as (
                                            e: unknown
                                        ) => string | number}
                                        .getLabel=${(pos: unknown) => (pos as Position).name}
                                        .getColor=${(pos: unknown) => app.getPositionColor(pos as Position)}
                                        .getIcon=${() => "icon.object.position"}
                                        class="slim"
                                    ></ptc-entity-multi-select>
                                </div>

                                <div class="horizontal spacing evenly stretching layout">
                                    <div class="field">
                                        <label>Beginn</label>

                                        <ptc-date-input name="start" datePicker="popover"></ptc-date-input>
                                    </div>

                                    <div class="field">
                                        <label>Ende</label>

                                        <ptc-date-input name="end" datePicker="popover"></ptc-date-input>
                                    </div>
                                </div>

                                <div class="horizontal spacing layout">
                                    <div class="field">
                                        <label>St. / Woche</label>
                                        <input
                                            type="number"
                                            name="hoursPerWeek"
                                            min="0"
                                            max="70"
                                            step="0.001"
                                            class="text-right-aligning slim"
                                        />
                                    </div>

                                    <div class="field stretch">
                                        <label>Bezahlung</label>

                                        <div class="horizontal layout">
                                            <div class="right icon input stretch">
                                                <input
                                                    type="number"
                                                    name="payAmount"
                                                    min="0"
                                                    step="0.01"
                                                    .value=${this.posting.payAmount?.toFixed(2) || ""}
                                                    class="slim pay-amount"
                                                />
                                                <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>
                                            </div>

                                            <select name="payType" class="slim pay-type">
                                                <option value="hourly" ?selected=${this.posting.payType === "hourly"}>
                                                    pro Stunde
                                                </option>
                                                <option value="monthly" ?selected=${this.posting.payType === "monthly"}>
                                                    monatlich
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label>Bilder</label>
                                    <div class="images">
                                        ${this._images.map(
                                            (img, index) =>
                                                html` <div class="image-container">
                                                    <img src="${img}" />
                                                    <button
                                                        type="button"
                                                        class="small subtle icon"
                                                        @click=${() => this._removeImage(index)}
                                                    >
                                                        <ptc-ui-icon name="icon.action.remove"></ptc-ui-icon>
                                                    </button>
                                                </div>`
                                        )}

                                        <button
                                            type="button"
                                            class="subtle"
                                            @click=${this._selectImage}
                                            ?hidden=${this._images.length >= 4}
                                        >
                                            <ptc-ui-icon name="icon.action.add-image" class="big"></ptc-ui-icon>
                                        </button>
                                    </div>
                                </div>

                                ${this._enabledFeatures?.recruitingUpdate
                                    ? html`
                                          <div>
                                              <label>Eingangsbestätigung</label>

                                              ${this._renderTemplatePreview(
                                                  this.posting.submissionConfirmationEmailTemplate,
                                                  "submission"
                                              )}
                                          </div>

                                          <div>
                                              <label>Automatische Absage</label>
                                              ${this._renderTemplatePreview(
                                                  this.posting.rejectionEmailTemplate,
                                                  "rejection"
                                              )}
                                          </div>
                                      `
                                    : nothing}
                            </div>
                        </ptc-scroller>
                    </div>
                </div>
            </form>

            <input
                type="file"
                style="position: absolute; opacity: 0; pointer-events: none;"
                @change=${this._imageSelected}
                accept=${[MimeType.PNG, MimeType.JPG, MimeType.WEBP].join(",")}
            />
        `;
    }

    private _renderApplications(status: JobApplicationStatus, applications: JobApplicationDTO[]) {
        const filteredApplications = applications.filter((a) => a.status === status);
        return html`
            <div class="padded applications">
                <label>${statusTitle[status]}</label>

                ${!filteredApplications?.length
                    ? html`<div class="small double-padded subtle box" style="display: inline-block">
                          Es liegen aktuell keine ${statusLabel[status]} vor.
                      </div>`
                    : ""}

                <div class="margined spacing grid">
                    ${filteredApplications?.map(
                        (application) => html`
                            <ptc-job-application-preview
                                .application=${application}
                                @application-click=${() => this._openApplication(application)}
                            ></ptc-job-application-preview>
                        `
                    )}
                </div>
            </div>
        `;
    }

    private _renderDisplay() {
        // @TODO: sanitize description and title
        const imageUrls = this.posting.imageUrls;
        return html`
            <div class="fullbleed vertical layout">
                <div class="padded horizontal center-aligning layout">
                    <div class="large bold padded stretch">${this.posting.title}</div>
                    ${this.posting.status === JobPostingStatus.Archived
                        ? html`
                              <button
                                  @click=${this._reactivatePosting}
                                  type="button"
                                  class="transparent"
                                  title="Ausschreibung reaktivieren"
                              >
                                  <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon>
                              </button>
                              <button
                                  class="transparent"
                                  @click=${this._copyPosting}
                                  type="button"
                                  title="Ausschreibung kopieren"
                              >
                                  <ptc-ui-icon name="icon.action.copy"></ptc-ui-icon>
                              </button>
                              <button
                                  class="transparent"
                                  @click=${this._deletePosting}
                                  type="button"
                                  title="Ausschreibung löschen"
                              >
                                  <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                              </button>
                          `
                        : html`
                              <button class="transparent" @click=${() => this.edit()} title="Ausschreibung bearbeiten">
                                  <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                              </button>

                              <button
                                  class="transparent"
                                  @click=${this._copyPosting}
                                  type="button"
                                  title="Ausschreibung kopieren"
                              >
                                  <ptc-ui-icon name="icon.action.copy"></ptc-ui-icon>
                              </button>

                              <button
                                  class="transparent"
                                  @click=${this._archivePosting}
                                  ?hidden=${!this.posting.id}
                                  title="Ausschreibung archivieren"
                              >
                                  <ptc-ui-icon name="icon.action.archive"></ptc-ui-icon>
                              </button>
                          `}
                </div>
                <ptc-scroller class="stretch">
                    <div class="padded spacing horizontal layout">
                        <div class="padded stretch">
                            <label>Beschreibung</label>
                            <ptc-rich-content
                                class="padded"
                                .content=${this.posting.description}
                                .previewHeight=${400}
                            ></ptc-rich-content>
                        </div>
                        <div class="sidebar">
                            <div class="padded sidebar-inner">
                                <label>Standort</label>

                                <div class="padded">${app.getVenue(this.posting.venueId)?.name}</div>

                                <label>Beschäftigungsverhältnis</label>

                                <div class="padded">${employmentTypeLabel(this.posting.employmentType)}</div>

                                <label>Positionen</label>

                                <div class="padded small pills stretch">
                                    ${this.posting.positions?.map(
                                        (position) => html`
                                            <div
                                                class="pill"
                                                style="--color-highlight: ${app.getPositionColor(position)}"
                                            >
                                                <ptc-ui-icon name="icon.object.position"></ptc-ui-icon>
                                                ${position.name}
                                            </div>
                                        `
                                    )}
                                </div>

                                <div class="horizontal evenly stretching layout">
                                    <div>
                                        <label>Beginn</label>

                                        <div class="padded">
                                            ${this.posting.start ? formatDate(this.posting.start) : "Ab Sofort"}
                                        </div>
                                    </div>

                                    <div>
                                        <label>Ende</label>

                                        <div class="padded">
                                            ${this.posting.end ? formatDate(this.posting.end) : "Unbegrenzt"}
                                        </div>
                                    </div>
                                </div>

                                <div class="horizontal evenly stretching layout">
                                    <div>
                                        <label>St./Woche</label>

                                        <div class="padded">
                                            ${this.posting.hoursPerWeek
                                                ? formatNumber(this.posting.hoursPerWeek)
                                                : "Keine Angabe"}
                                        </div>
                                    </div>

                                    <div>
                                        <label>Bezahlung</label>

                                        <div class="padded">
                                            ${this.posting.payAmount
                                                ? `
                                                      ${formatNumber(this.posting.payAmount)} € /
                                                      ${this.posting.payType === "monthly" ? "Monat" : "Stunde"}
                                                  `
                                                : "Keine Angabe"}
                                        </div>
                                    </div>
                                </div>

                                ${imageUrls.length
                                    ? html`
                                          <label>Bilder</label>

                                          <div class="images">
                                              ${imageUrls.map(
                                                  (img: string) =>
                                                      html` <div class="image-container">
                                                          <img src="${img}" />
                                                      </div>`
                                              )}
                                          </div>
                                      `
                                    : ""}

                                <label>Bewerbungs-Link</label>

                                <div class="padded break-all">
                                    ${this.posting.status === JobPostingStatus.Archived
                                        ? html`<span class="subtle">archiviert – kein Link verfügbar</span>`
                                        : html`
                                              <a
                                                  href="${PUBLIC_PTC_JOBS_URL}/postings/${this.posting.uuid}"
                                                  target="_blank"
                                              >
                                                  ${PUBLIC_PTC_JOBS_URL}/postings/${this.posting.uuid}
                                              </a>
                                          `}
                                </div>
                            </div>
                        </div>
                    </div>

                    ${this._renderApplications(JobApplicationStatus.Open, this.posting.applications || [])}
                    ${this._renderApplications(JobApplicationStatus.Accepted, this.posting.applications || [])}
                    ${this._renderApplications(JobApplicationStatus.Rejected, this.posting.applications || [])}
                </ptc-scroller>
            </div>
        `;
    }

    render() {
        if (!this.posting) {
            return;
        }

        return html`
            ${this._editing ? this._renderEditing() : this._renderDisplay()}

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
